/**
 * Bucket B opt 4 — "List your property for investment".
 *
 * Logged-out users get a sign-in/register prompt. Signed-in users fill a focused
 * property + investment-conditions form that is submitted for MANUAL admin review
 * (POST /api/investors/listing → an IndividualPropertyRequest, status
 * pending_review). The licence/land-id/deed are checked by an admin before
 * anything is published — so this is intentionally NOT the live listing pipeline.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../hooks/useAuth';
import { submitInvestmentListing, type InvestmentListingInput } from '../../services/investorsService';

// Curated investable property types (the backend accepts the full PropertyType set).
const PROPERTY_TYPES: { value: string; en: string; ar: string }[] = [
  { value: 'LAND', en: 'Land', ar: 'أرض' },
  { value: 'RESIDENTIAL_LAND', en: 'Residential land', ar: 'أرض سكنية' },
  { value: 'COMMERCIAL_LAND', en: 'Commercial land', ar: 'أرض تجارية' },
  { value: 'AGRICULTURAL_LAND', en: 'Agricultural land', ar: 'أرض زراعية' },
  { value: 'RESIDENTIAL_BUILDING', en: 'Residential building', ar: 'مبنى سكني' },
  { value: 'COMMERCIAL_BUILDING', en: 'Commercial building', ar: 'مبنى تجاري' },
  { value: 'VILLA', en: 'Villa', ar: 'فيلا' },
  { value: 'APARTMENT', en: 'Apartment', ar: 'شقة' },
  { value: 'FARM', en: 'Farm', ar: 'مزرعة' },
  { value: 'WAREHOUSE', en: 'Warehouse', ar: 'مستودع' },
  { value: 'OFFICE', en: 'Office', ar: 'مكتب' },
  { value: 'MALL', en: 'Mall', ar: 'مول' },
  { value: 'HOTEL', en: 'Hotel', ar: 'فندق' },
];

const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-[#004D40] focus:ring-2 focus:ring-[#004D40]/30';
const labelCls = 'mb-1 block text-sm font-medium text-slate-700';

export default function ListForInvestmentPage() {
  const { i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';
  const { user, session } = useAuth();
  const token = session?.token ?? null;

  const [form, setForm] = useState<Record<string, string>>({
    title: '', description: '', propertyType: 'LAND', address: '', price: '',
    ownerName: user?.fullName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || '',
    ownerPhone: user?.phoneNumber || '', ownerEmail: user?.email || '',
    investmentMinContribution: '', investmentFundingGoal: '', investmentExpectedRoi: '',
    investmentDeadline: '', investmentTerms: '', deedNumber: '', landIdNumber: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const num = (v: string) => { const n = Number(v); return v.trim() !== '' && Number.isFinite(n) ? n : undefined; };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError('');
    if (form.title.trim().length < 3 || form.ownerName.trim().length < 2 || form.ownerPhone.trim().length < 5) {
      setError(isRTL ? 'يرجى تعبئة العنوان واسم المالك ورقم الهاتف.' : 'Please fill the title, owner name and phone.');
      return;
    }
    setBusy(true);
    try {
      const payload: InvestmentListingInput = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        propertyType: form.propertyType,
        address: form.address.trim() || undefined,
        price: num(form.price),
        ownerName: form.ownerName.trim(),
        ownerPhone: form.ownerPhone.trim(),
        ownerEmail: form.ownerEmail.trim() || undefined,
        investmentMinContribution: num(form.investmentMinContribution),
        investmentFundingGoal: num(form.investmentFundingGoal),
        investmentExpectedRoi: num(form.investmentExpectedRoi),
        investmentDeadline: form.investmentDeadline || undefined,
        investmentTerms: form.investmentTerms.trim() || undefined,
        deedNumber: form.deedNumber.trim() || undefined,
        landIdNumber: form.landIdNumber.trim() || undefined,
      };
      await submitInvestmentListing(token, payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err instanceof Error ? err.message : (isRTL ? 'تعذّر الإرسال. حاول مجدداً.' : 'Submission failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main id="main-content" className="pb-20">
        <section className="bg-[#003930] py-12 text-white">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <Link to="/investors" className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/80 hover:text-white">
              <i className={isRTL ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} aria-hidden="true" />{isRTL ? 'منصة الاستثمار' : 'Investment hub'}
            </Link>
            <h1 className="font-display text-3xl font-bold sm:text-4xl"><i className="ri-coins-line mx-2" aria-hidden="true" />{isRTL ? 'اعرض عقارك للاستثمار' : 'List your property for investment'}</h1>
            <p className="mt-3 text-sm text-white/85 sm:text-base">{isRTL ? 'أضف عقارك وشروط الاستثمار - يراجعه فريقنا يدوياً ويتواصل معك.' : 'Add your property + investment terms - our team reviews it manually and gets back to you.'}</p>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 py-8">
          {/* Logged-out gate */}
          {!user ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]"><i className="ri-lock-2-line text-2xl text-[#004D40]" aria-hidden="true" /></div>
              <h2 className="font-display text-lg font-semibold text-[var(--pv-color-primary)]">{isRTL ? 'سجّل الدخول للمتابعة' : 'Sign in to continue'}</h2>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{isRTL ? 'تحتاج إلى حساب لعرض عقارك للاستثمار.' : 'You need an account to list your property for investment.'}</p>
              <div className="mt-5 flex items-center justify-center gap-3">
                <Link to="/signin" className="rounded-full bg-[#004D40] px-6 py-2.5 text-sm font-semibold text-white">{isRTL ? 'تسجيل الدخول' : 'Sign in'}</Link>
                <Link to="/register" className="rounded-full border border-[var(--pv-color-accent-soft)] px-6 py-2.5 text-sm font-semibold text-[#003930] hover:bg-[var(--pv-color-surface-sunken)]">{isRTL ? 'إنشاء حساب' : 'Register'}</Link>
              </div>
            </div>
          ) : done ? (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100"><i className="ri-checkbox-circle-fill text-3xl text-green-500" aria-hidden="true" /></div>
              <h2 className="font-display text-lg font-semibold text-[var(--pv-color-primary)]">{isRTL ? 'تم استلام طلبك' : 'Your submission was received'}</h2>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">{isRTL ? 'سيراجع فريقنا العقار والمستندات ويتواصل معك قريباً.' : 'Our team will review the property + documents and get back to you soon.'}</p>
              <Link to="/investors" className="mt-5 inline-block rounded-full bg-[#004D40] px-6 py-2.5 text-sm font-semibold text-white">{isRTL ? 'العودة للمنصة' : 'Back to the hub'}</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" dir={i18n.dir()}>
              {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</div>}

              {/* Property */}
              <fieldset className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <legend className="px-2 text-sm font-semibold text-slate-700">{isRTL ? 'بيانات العقار' : 'Property details'}</legend>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls} htmlFor="iv-title">{isRTL ? 'عنوان العرض' : 'Listing title'} <span className="text-red-500">*</span></label>
                    <input id="iv-title" className={inputCls} value={form.title} onChange={set('title')} placeholder={isRTL ? 'مثال: أرض تجارية للتطوير في الرياض' : 'e.g. Commercial land for development in Riyadh'} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="iv-type">{isRTL ? 'نوع العقار' : 'Property type'}</label>
                      <select id="iv-type" className={inputCls} value={form.propertyType} onChange={set('propertyType')}>
                        {PROPERTY_TYPES.map((p) => <option key={p.value} value={p.value}>{isRTL ? p.ar : p.en}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="iv-price">{isRTL ? 'السعر التقديري (ريال)' : 'Estimated price (SAR)'}</label>
                      <input id="iv-price" className={inputCls} value={form.price} onChange={set('price')} inputMode="numeric" placeholder="-" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-address">{isRTL ? 'الموقع / العنوان' : 'Location / address'}</label>
                    <input id="iv-address" className={inputCls} value={form.address} onChange={set('address')} placeholder={isRTL ? 'المدينة، الحي…' : 'City, district…'} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-desc">{isRTL ? 'الوصف' : 'Description'}</label>
                    <textarea id="iv-desc" className={`${inputCls} min-h-[90px]`} value={form.description} onChange={set('description')} placeholder={isRTL ? 'صف العقار وفرصة التطوير…' : 'Describe the property & development opportunity…'} />
                  </div>
                </div>
              </fieldset>

              {/* Investment conditions */}
              <fieldset className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <legend className="px-2 text-sm font-semibold text-slate-700">{isRTL ? 'شروط الاستثمار' : 'Investment conditions'}</legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="iv-min">{isRTL ? 'أقل مساهمة (ريال)' : 'Minimum contribution (SAR)'}</label>
                    <input id="iv-min" className={inputCls} value={form.investmentMinContribution} onChange={set('investmentMinContribution')} inputMode="numeric" placeholder="-" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-goal">{isRTL ? 'هدف التمويل (ريال)' : 'Funding goal (SAR)'}</label>
                    <input id="iv-goal" className={inputCls} value={form.investmentFundingGoal} onChange={set('investmentFundingGoal')} inputMode="numeric" placeholder="-" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-roi">{isRTL ? 'العائد المتوقع (%)' : 'Expected ROI (%)'}</label>
                    <input id="iv-roi" className={inputCls} value={form.investmentExpectedRoi} onChange={set('investmentExpectedRoi')} inputMode="decimal" placeholder="-" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-deadline">{isRTL ? 'آخر موعد' : 'Deadline'}</label>
                    <input id="iv-deadline" type="date" className={inputCls} value={form.investmentDeadline} onChange={set('investmentDeadline')} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="iv-terms">{isRTL ? 'شروط / ملاحظات' : 'Terms / notes'}</label>
                    <textarea id="iv-terms" className={`${inputCls} min-h-[70px]`} value={form.investmentTerms} onChange={set('investmentTerms')} placeholder={isRTL ? 'هيكل الحصص، حصة الأرباح…' : 'Equity structure, profit share…'} />
                  </div>
                </div>
              </fieldset>

              {/* Owner + verification */}
              <fieldset className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <legend className="px-2 text-sm font-semibold text-slate-700">{isRTL ? 'بيانات المالك والتحقق' : 'Owner & verification'}</legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="iv-owner">{isRTL ? 'اسم المالك' : 'Owner name'} <span className="text-red-500">*</span></label>
                    <input id="iv-owner" className={inputCls} value={form.ownerName} onChange={set('ownerName')} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-phone">{isRTL ? 'رقم الهاتف' : 'Phone'} <span className="text-red-500">*</span></label>
                    <input id="iv-phone" type="tel" className={inputCls} value={form.ownerPhone} onChange={set('ownerPhone')} placeholder="+9665XXXXXXXX" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-email">{isRTL ? 'البريد الإلكتروني' : 'Email'}</label>
                    <input id="iv-email" type="email" className={inputCls} value={form.ownerEmail} onChange={set('ownerEmail')} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-deed">{isRTL ? 'رقم الصك' : 'Deed number'}</label>
                    <input id="iv-deed" className={inputCls} value={form.deedNumber} onChange={set('deedNumber')} placeholder="-" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="iv-land">{isRTL ? 'رقم الأرض / الرخصة' : 'Land / licence ID'}</label>
                    <input id="iv-land" className={inputCls} value={form.landIdNumber} onChange={set('landIdNumber')} placeholder="-" />
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-400">{isRTL ? 'يتم التحقق من المستندات يدوياً قبل أي نشر.' : 'Documents are verified manually before anything is published.'}</p>
              </fieldset>

              <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#004D40] px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
                {busy ? (isRTL ? 'جارٍ الإرسال…' : 'Submitting…') : (isRTL ? 'إرسال للمراجعة' : 'Submit for review')}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
